let _workspaceId = "";

export function setCacheWorkspaceId(id) {
  _workspaceId = id || "";
}

function getDBName() {
  return _workspaceId ? `bia_msg_cache_${_workspaceId}` : "bia_msg_cache";
}

const DB_VERSION = 2;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

function openDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(getDBName(), DB_VERSION);
    request.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains("messages")) {
        const msgStore = db.createObjectStore("messages", { keyPath: "messageId" });
        msgStore.createIndex("byCanal", "canal", { unique: false });
      }
      if (!db.objectStoreNames.contains("meta")) {
        db.createObjectStore("meta", { keyPath: "roomId" });
      }
      if (!db.objectStoreNames.contains("lists")) {
        db.createObjectStore("lists", { keyPath: "key" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function cacheMessages(roomId, messages) {
  try {
    const db = await openDB();
    const tx = db.transaction(["messages", "meta"], "readwrite");
    const msgStore = tx.objectStore("messages");
    const metaStore = tx.objectStore("meta");
    for (const msg of messages) {
      msgStore.put(msg);
    }
    metaStore.put({ roomId, cachedAt: Date.now() });
    return new Promise((resolve, reject) => {
      tx.oncomplete = () => { db.close(); resolve(); };
      tx.onerror = () => { db.close(); reject(tx.error); };
    });
  } catch (err) {
    console.error("cacheMessages error:", err);
  }
}

export async function getCachedMessages(roomId) {
  try {
    const db = await openDB();
    const tx = db.transaction(["messages", "meta"], "readonly");
    const metaStore = tx.objectStore("meta");
    const msgStore = tx.objectStore("messages");
    const index = msgStore.index("byCanal");

    const meta = await new Promise((resolve) => {
      const req = metaStore.get(roomId);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
    });

    if (!meta || Date.now() - meta.cachedAt > CACHE_TTL) {
      db.close();
      return null;
    }

    const messages = await new Promise((resolve) => {
      const req = index.getAll(roomId);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve([]);
    });

    db.close();
    return messages.sort((a, b) => b.timeUnix - a.timeUnix);
  } catch (err) {
    console.error("getCachedMessages error:", err);
    return null;
  }
}

export async function cacheSingleMessage(msg) {
  try {
    const db = await openDB();
    const tx = db.transaction("messages", "readwrite");
    tx.objectStore("messages").put(msg);
    return new Promise((resolve) => {
      tx.oncomplete = () => { db.close(); resolve(); };
      tx.onerror = () => { db.close(); resolve(); };
    });
  } catch (err) {
    console.error("cacheSingleMessage error:", err);
  }
}

export async function removeCachedMessage(messageId) {
  try {
    const db = await openDB();
    const tx = db.transaction("messages", "readwrite");
    tx.objectStore("messages").delete(messageId);
    return new Promise((resolve) => {
      tx.oncomplete = () => { db.close(); resolve(); };
      tx.onerror = () => { db.close(); resolve(); };
    });
  } catch (err) {
    console.error("removeCachedMessage error:", err);
  }
}

export async function cacheList(key, data) {
  try {
    const db = await openDB();
    const tx = db.transaction("lists", "readwrite");
    tx.objectStore("lists").put({ key, data, cachedAt: Date.now() });
    return new Promise((resolve) => {
      tx.oncomplete = () => { db.close(); resolve(); };
      tx.onerror = () => { db.close(); resolve(); };
    });
  } catch (err) {
    console.error("cacheList error:", err);
  }
}

export async function getCachedList(key) {
  try {
    const db = await openDB();
    const tx = db.transaction("lists", "readonly");
    const result = await new Promise((resolve) => {
      const req = tx.objectStore("lists").get(key);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
    });
    db.close();
    if (!result || Date.now() - result.cachedAt > CACHE_TTL) {
      return null;
    }
    return result.data;
  } catch (err) {
    console.error("getCachedList error:", err);
    return null;
  }
}

export async function clearMessageCache() {
  try {
    const db = await openDB();
    const tx = db.transaction(["messages", "meta", "lists"], "readwrite");
    tx.objectStore("messages").clear();
    tx.objectStore("meta").clear();
    tx.objectStore("lists").clear();
    return new Promise((resolve) => {
      tx.oncomplete = () => { db.close(); resolve(); };
      tx.onerror = () => { db.close(); resolve(); };
    });
  } catch (err) {
    console.error("clearMessageCache error:", err);
  }
}
